import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, Loader2, Plus, ArrowUp, Square, Wrench, Brain, Zap, Copy, Check, Bug, MessageCircle, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  ExpandableChat,
  ExpandableChatHeader,
  ExpandableChatBody,
  ExpandableChatFooter,
} from "@/components/ui/expandable-chat";
import {
  ChatBubble,
  ChatBubbleMessage,
  ChatBubbleAvatar,
} from "@/components/ui/chat-bubble";
import { ChatMessageList } from "@/components/ui/chat-message-list";
import { LiveStatusLine } from "@/components/agents/LiveStatusLine";
import { Textarea } from "@/components/ui/textarea";
import { VoiceMicButton } from "@/components/ai/VoiceMicButton";
import { VoiceEnrollmentDialog } from "@/components/voice/VoiceEnrollmentDialog";
import { useVoiceControl } from "@/hooks/useVoiceControl";
import { useChatStore } from "@/stores/agents/chat-store";
import { userAgentApi } from "@/services/api/agentApi";
import { UserAgentProfile } from "@/components/agents/UserAgentProfile";
import { SkillsModal } from "@/components/agents/SkillsModal";
import { useViewerUI } from "@/stores/viewer/viewer-store";
import { useAuth } from "@/stores/auth/auth-store";
import { FeedbackForm } from "@/components/feedback/FeedbackWidget";
import { ExpandableChatMessages } from "@/components/chat/ExpandableChatMessages";
import { useUnreadCount } from "@/stores/messaging";
import { SHADOWS } from "@/styles/tokens";

function isAgentNamingQuestion(question: string): boolean {
  return /what would you like to call me|pick any name/i.test(question);
}

export const GlobalExpandableChat: React.FC = () => {
  const { isAuthenticated, initialized } = useAuth();
  const location = useLocation();

  // Hide on editor routes (AIPropertiesChat in sidebar handles chat there)
  const isEditorRoute = location.pathname.includes('/editor/');

  const {
    threads,
    activeThreadId,
    messages,
    isLoadingMessages,
    isSending,
    chatStatus,
    agentName,
    fetchThreads,
    loadThread,
    startNewChat,
    sendMessage,
    cancelSend,
    fetchStatus,
    setChatMode,
    initUserAgentSocket,
    fetchAgentName,
  } = useChatStore();
  const { isFullscreen } = useViewerUI();
  const {
    voiceState, showEnrollment, setShowEnrollment,
    error: voiceError, handleMicPress, handleStopSpeaking,
    handleSpeakResponse, onEnrollmentComplete, setOnTranscription,
  } = useVoiceControl();

  const unreadCount = useUnreadCount();
  const [activeTab, setActiveTab] = useState<'chat' | 'messages' | 'feedback'>('chat');
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [onboardingAnswers, setOnboardingAnswers] = useState<Record<string, string>>({});
  const [isSubmittingOnboarding, setIsSubmittingOnboarding] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [sessionCopied, setSessionCopied] = useState(false);

  /** Copy a single message — skips if the user is selecting text. */
  const copyMessage = (messageId: string, content: string) => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;
    if (!content) return;
    navigator.clipboard.writeText(content).then(
      () => {
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId((prev) => (prev === messageId ? null : prev)), 1500);
      },
      () => { /* clipboard unavailable — silent */ },
    );
  };

  /** Copy the entire active conversation as a plain-text transcript. */
  const copySession = () => {
    if (messages.length === 0) return;
    const transcript = messages
      .filter((m) => !m.isProgress && m.content)
      .map((m) => {
        const who = m.role === "user" ? "You" : (agentName || "Agent");
        const ts = m.timestamp.toLocaleString([], { dateStyle: "short", timeStyle: "short" });
        return `[${ts}] ${who}:\n${m.content}`;
      })
      .join("\n\n");
    if (!transcript) return;
    navigator.clipboard.writeText(transcript).then(
      () => {
        setSessionCopied(true);
        setTimeout(() => setSessionCopied(false), 1500);
      },
      () => { /* clipboard unavailable — silent */ },
    );
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingAutoSendRef = useRef(false);

  // User agent is always available; status dot shows agent service health
  const agentOnline = chatStatus?.available ?? true;
  // In user_agent mode, isSending is not set; detect thinking from message status
  const isThinking = messages.some((m) => m.status === "thinking");
  const isBusy = isSending || isThinking;

  // Initialize user agent mode on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    setChatMode("user_agent");
    initUserAgentSocket();
    fetchStatus();
    fetchThreads();
    fetchAgentName();
  }, [
    fetchAgentName,
    fetchStatus,
    fetchThreads,
    initUserAgentSocket,
    isAuthenticated,
    setChatMode,
  ]);

  // Auto-load the most recent thread on first fetch if none is active
  useEffect(() => {
    if (threads.length > 0 && activeThreadId === null) {
      loadThread(threads[0].thread_id);
    }
  }, [activeThreadId, loadThread, threads]);

  // Wire voice transcription to auto-send, then stop listening (single-shot)
  useEffect(() => {
    setOnTranscription((text: string) => {
      setInputValue(text);
      pendingAutoSendRef.current = true;
      // Stop listening after transcription — deferred so VAD callback chain completes first
      setTimeout(() => handleMicPress(), 0);
    });
  }, [setOnTranscription, handleMicPress]);

  // Auto-TTS for assistant responses
  const prevMessageCountRef = useRef(messages.length);
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.role === "assistant" && lastMsg.content) {
        handleSpeakResponse(lastMsg.content);
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages, handleSpeakResponse]);

  const handleSend = React.useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isBusy) return;

    setInputValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    await sendMessage(text);
    textareaRef.current?.focus();
  }, [inputValue, isBusy, sendMessage]);

  // Auto-send when voice transcription populates inputValue
  useEffect(() => {
    if (pendingAutoSendRef.current && inputValue.trim()) {
      pendingAutoSendRef.current = false;
      void handleSend();
    }
  }, [handleSend, inputValue]);

  const handleOnboardingSubmit = async (messageId: string, question: string) => {
    const answer = (onboardingAnswers[messageId] ?? "").trim();
    if (!answer || isSubmittingOnboarding) return;
    setIsSubmittingOnboarding(true);
    try {
      const res = await userAgentApi.submitOnboardingAnswer(question, answer);
      setOnboardingAnswers((prev) => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });
      useChatStore.setState((s) => {
        const messages = [...s.messages];
        const resolvedMessageIndex = messages.findIndex((msg) => msg.id === messageId);
        const nextAgentName = isAgentNamingQuestion(question) && answer.toLowerCase() !== "skip"
          ? answer
          : s.agentName;

        if (resolvedMessageIndex !== -1) {
          const resolvedMessage = messages[resolvedMessageIndex];
          if (resolvedMessage.content.trim()) {
            messages[resolvedMessageIndex] = {
              ...resolvedMessage,
              onboardingQuestion: null,
            };
          } else {
            messages.splice(resolvedMessageIndex, 1);
          }
        }

        messages.push({
          id: `onboarding-answer-${Date.now()}`,
          role: "user",
          content: answer,
          timestamp: new Date(),
          status: "done",
        });

        if (res.data.nextQuestion) {
          messages.push({
            id: `onboarding-${Date.now()}`,
            role: "assistant",
            content: "",
            timestamp: new Date(),
            onboardingQuestion: res.data.nextQuestion,
          });
        } else if (res.data.onboardingComplete) {
          messages.push({
            id: `onboarding-done-${Date.now()}`,
            role: "assistant",
            content: "Onboarding complete! I now have a better understanding of how to work with you.",
            timestamp: new Date(),
          });
        }

        return {
          messages,
          agentName: nextAgentName,
        };
      });
    } catch {
      // User can retry
    } finally {
      setIsSubmittingOnboarding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  // Don't render if not authenticated
  if (!initialized || !isAuthenticated) return null;

  // Hide on editor routes
  if (isEditorRoute) return null;

  // Hide during fullscreen (viewer pages)
  if (isFullscreen) return null;

  return (
    <ExpandableChat
      size="lg"
      position="bottom-right"
      icon={<Sparkles className="h-6 w-6" />}
    >
      {/* Header */}
      <ExpandableChatHeader>
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'hsl(var(--primary))' }}
          >
            {activeTab === 'feedback'
              ? <Bug className="w-4 h-4 text-white" />
              : activeTab === 'messages'
                ? <MessageCircle className="w-4 h-4 text-white" />
                : <Sparkles className="w-4 h-4 text-white" />}
          </div>
          <span className="text-sm font-semibold text-foreground">
            {activeTab === 'feedback'
              ? 'Send Feedback'
              : activeTab === 'messages'
                ? 'Messages'
                : (agentName || 'My Agent')}
          </span>
          {activeTab === 'chat' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: agentOnline
                        ? 'hsl(var(--feedback-success, 160 71% 45%))'
                        : 'hsl(var(--destructive))',
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">
                    {agentOnline ? "Online" : "Offline"}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Chat toggle — visible when on messages or feedback */}
          {activeTab !== 'chat' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    aria-label="Back to chat"
                    onClick={() => setActiveTab('chat')}
                  >
                    <Sparkles className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Chat</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Messages toggle — hidden when already on messages */}
          {activeTab !== 'messages' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 relative"
                    aria-label="Messages"
                    onClick={() => setActiveTab('messages')}
                  >
                    <MessageCircle className="w-4 h-4" />
                    {unreadCount > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[10px] font-semibold leading-none"
                        style={{
                          backgroundColor: 'hsl(var(--primary))',
                          color: 'hsl(var(--primary-foreground))',
                        }}
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Messages</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Feedback toggle — hidden when already on feedback */}
          {activeTab !== 'feedback' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    aria-label="Send feedback"
                    onClick={() => setActiveTab('feedback')}
                  >
                    <Bug className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Feedback</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Chat actions menu (ellipsis) — only visible on chat tab */}
          {activeTab === 'chat' && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  aria-label="Chat options"
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" side="bottom" className="w-[340px] p-0">
                {/* Quick actions */}
                <div className="flex flex-col border-b">
                  <button
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left text-foreground hover:bg-muted transition-colors"
                    onClick={copySession}
                    disabled={messages.length === 0}
                  >
                    {sessionCopied ? (
                      <Check className="w-4 h-4" style={{ color: 'hsl(var(--primary))' }} />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    {sessionCopied ? 'Copied!' : 'Copy conversation'}
                  </button>
                  <button
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left text-foreground hover:bg-muted transition-colors"
                    onClick={() => startNewChat()}
                  >
                    <Plus className="w-4 h-4" />
                    New chat
                  </button>
                </div>
                {/* Thread history */}
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="text-xs font-medium text-muted-foreground">
                    History
                  </span>
                </div>
                <ScrollArea className="max-h-[240px]">
                  {threads.length === 0 ? (
                    <p className="text-xs p-4 text-center text-muted-foreground">
                      No conversations yet
                    </p>
                  ) : (
                    threads.map((thread) => (
                      <button
                        key={thread.thread_id}
                        onClick={() => {
                          loadThread(thread.thread_id);
                        }}
                        className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-b"
                        style={{
                          borderLeft:
                            activeThreadId === thread.thread_id
                              ? `3px solid hsl(var(--primary))`
                              : "3px solid transparent",
                        }}
                      >
                        <p className="text-sm truncate text-foreground">
                          {thread.title || "Untitled"}
                        </p>
                        <p className="text-[10px] mt-0.5 text-muted-foreground">
                          {timeAgo(thread.updated_at)}
                          {thread.turn_count > 1 &&
                            ` \u00B7 ${thread.turn_count} turns`}
                        </p>
                      </button>
                    ))
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </ExpandableChatHeader>

      {/* Body — switches between chat, messages, and feedback */}
      {activeTab === 'feedback' ? (
        <ExpandableChatBody className="bg-background flex flex-col">
          <FeedbackForm onSuccess={() => setActiveTab('chat')} />
        </ExpandableChatBody>
      ) : activeTab === 'messages' ? (
        <ExpandableChatBody className="bg-background flex flex-col p-0">
          <ExpandableChatMessages />
        </ExpandableChatBody>
      ) : (
        <>
          <ExpandableChatBody className="bg-background">
            {isLoadingMessages ? (
              <div className="h-full flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Sparkles
                  className="w-10 h-10 mb-3 opacity-40"
                  style={{ color: 'hsl(var(--primary))' }}
                />
                <p className="text-sm font-medium mb-1 text-foreground">
                  {agentName ? `Hi, I'm ${agentName}` : 'Your Personal Agent'}
                </p>
                <p className="text-xs text-center px-6 text-muted-foreground">
                  {agentName
                    ? "I'm your personal AI agent. I learn your preferences and can coordinate with the full agent community on your behalf."
                    : 'Chat with your personal AI agent. It learns your preferences and can coordinate with the full agent community on your behalf.'}
                </p>
              </div>
            ) : (
              <ChatMessageList smooth>
                {messages.map((msg) => {
                  const onboardingQuestion = msg.onboardingQuestion;

                  return (
                    <React.Fragment key={msg.id}>
                      {msg.role === 'assistant' && msg.status === 'thinking' && (
                        <LiveStatusLine messageId={msg.id} />
                      )}
                      <ChatBubble
                        key={msg.id}
                        variant={msg.role === "user" ? "sent" : "received"}
                      >
                        <ChatBubbleAvatar
                          className="h-8 w-8 shrink-0"
                          fallback={msg.role === "user" ? "You" : (agentName ? agentName.charAt(0).toUpperCase() : "AI")}
                        />
                        <ChatBubbleMessage
                          variant={msg.role === "user" ? "sent" : "received"}
                          isLoading={msg.status === "thinking"}
                          className={
                            msg.role === "user" ? "" : ""
                          }
                          onClick={() => msg.content && msg.status !== "thinking" && copyMessage(msg.id, msg.content)}
                          title="Click to copy"
                          style={{
                            maxWidth: "85%",
                            backgroundColor:
                              msg.role === "user"
                                ? 'hsl(var(--primary))'
                                : 'hsl(var(--muted))',
                            color:
                              msg.role === "user" ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                            cursor: msg.content && msg.status !== "thinking" ? "pointer" : "default",
                            boxShadow:
                              copiedMessageId === msg.id
                                ? `0 0 0 2px hsl(var(--primary) / 0.4)`
                                : undefined,
                            transition: "box-shadow 150ms ease",
                            position: "relative",
                          }}
                        >
                          {copiedMessageId === msg.id && (
                            <span
                              className="absolute top-1 right-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: 'hsl(var(--primary))',
                                color: 'hsl(var(--primary-foreground))',
                              }}
                            >
                              <Check className="w-3 h-3" />
                              Copied
                            </span>
                          )}
                          {msg.content && (
                            <p className="text-sm whitespace-pre-wrap">
                              {msg.content}
                            </p>
                          )}
                          {msg.clarification && msg.clarification.options.length > 0 && (
                            <div
                              className="mt-2 flex flex-wrap gap-1.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {msg.clarification.options.map((opt) => (
                                <button
                                  key={`${msg.id}-opt-${opt.intent}-${opt.label}`}
                                  type="button"
                                  onClick={() => {
                                    const topic = msg.clarification?.topic || '';
                                    const reply = topic
                                      ? `${opt.label}: ${topic}`
                                      : opt.label;
                                    void sendMessage(reply);
                                  }}
                                  className="px-2.5 py-1 rounded-full text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1"
                                  style={{
                                    backgroundColor: 'hsl(var(--primary) / 0.08)',
                                    color: 'hsl(var(--primary))',
                                    border: '1px solid hsl(var(--primary) / 0.25)',
                                  }}
                                  aria-label={`Use ${opt.label}`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          )}
                          {onboardingQuestion && (
                            <div
                              className="mt-2 p-3 rounded-md"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                backgroundColor: 'hsl(var(--primary) / 0.06)',
                                border: '1px solid hsl(var(--primary) / 0.2)',
                              }}
                            >
                              <p className="text-sm font-medium mb-2 text-foreground">
                                {onboardingQuestion}
                              </p>
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  className="flex-1 px-3 py-1.5 rounded-md text-sm focus:outline-none focus:ring-2 border text-foreground"
                                  style={{
                                    backgroundColor: 'hsl(var(--card))',
                                    fontSize: "13px",
                                  }}
                                  placeholder="Your answer..."
                                  value={onboardingAnswers[msg.id] ?? ""}
                                  onChange={(e) =>
                                    setOnboardingAnswers((prev) => ({
                                      ...prev,
                                      [msg.id]: e.target.value,
                                    }))
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleOnboardingSubmit(msg.id, onboardingQuestion);
                                    }
                                  }}
                                  disabled={isSubmittingOnboarding}
                                />
                                <Button
                                  size="sm"
                                  type="button"
                                  onClick={() => handleOnboardingSubmit(msg.id, onboardingQuestion)}
                                  disabled={!(onboardingAnswers[msg.id] ?? "").trim() || isSubmittingOnboarding}
                                  style={{
                                    backgroundColor: 'hsl(var(--primary))',
                                    color: 'hsl(var(--primary-foreground))',
                                  }}
                                >
                                  {isSubmittingOnboarding ? "..." : "Answer"}
                                </Button>
                              </div>
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className="text-[10px]"
                              style={{ opacity: 0.6 }}
                            >
                              {msg.timestamp.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                            {msg.usage && (
                              <span
                                className="text-[10px]"
                                style={{ opacity: 0.6 }}
                              >
                                {msg.usage.modelName}
                              </span>
                            )}
                          </div>
                        </ChatBubbleMessage>
                      </ChatBubble>
                    </React.Fragment>
                  );
                })}

                {isBusy &&
                  !messages.some((m) => m.status === "thinking") && (
                    <ChatBubble variant="received">
                      <ChatBubbleAvatar
                        className="h-8 w-8 shrink-0"
                        fallback={agentName ? agentName.charAt(0).toUpperCase() : "AI"}
                      />
                      <ChatBubbleMessage isLoading />
                    </ChatBubble>
                  )}
              </ChatMessageList>
            )}
          </ExpandableChatBody>

          {/* Footer — only shown in chat mode */}
          <ExpandableChatFooter className="border-t-0 p-2 bg-background">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
            >
              {/* Rounded prompt container */}
              <div
                style={{
                  border: isFocused ? '1px solid hsl(var(--primary) / 0.7)' : '1px solid hsl(var(--border))',
                  borderRadius: '28px',
                  backgroundColor: 'hsl(var(--card))',
                  boxShadow: SHADOWS.sm,
                  transition: 'border-color 150ms ease-in-out',
                }}
              >
                {/* Textarea */}
                <div style={{ padding: '12px 20px 0' }}>
                  <Textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder="Talk to your agent..."
                    disabled={isBusy}
                    className="w-full resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 bg-transparent"
                    rows={1}
                    style={{
                      fontSize: '14px',
                      lineHeight: '1.5',
                      minHeight: '24px',
                      maxHeight: '200px',
                      overflow: 'auto',
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                      backgroundColor: 'transparent',
                    }}
                  />
                </div>

                {/* Bottom toolbar */}
                <div
                  className="flex items-center justify-between"
                  style={{ padding: '4px 8px 8px 12px' }}
                >
                  {/* Left group */}
                  <div className="flex items-center gap-1">
                    {/* + Button - placeholder for future file attachment */}
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      disabled
                      aria-label="Attach"
                      className="h-8 w-8 rounded-full text-muted-foreground opacity-40"
                    >
                      <Plus className="w-5 h-5" />
                    </Button>

                    {/* Tools Button - Popover */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 rounded-full px-3 gap-1.5 text-muted-foreground"
                          aria-label="Tools menu"
                        >
                          <Wrench className="w-3.5 h-3.5" />
                          <span className="text-xs">Tools</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top"
                        align="start"
                        className="w-52 p-1"
                        sideOffset={8}
                      >
                        <div className="flex flex-col">
                          {/* Agent Profile */}
                          <button
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                            onClick={() => setShowProfileDialog(true)}
                          >
                            <Brain className="w-4 h-4" />
                            Agent Profile
                          </button>
                          {/* Skills */}
                          <button
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md text-left text-foreground hover:bg-muted transition-colors"
                            onClick={() => setShowSkillsModal(true)}
                          >
                            <Zap className="w-4 h-4" />
                            Skills
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Right group */}
                  <div className="flex items-center gap-1">
                    {/* Voice Mic - compact mode */}
                    <VoiceMicButton
                      voiceState={voiceState}
                      onPress={handleMicPress}
                      onStopSpeaking={handleStopSpeaking}
                      disabled={isBusy}
                      error={voiceError}
                      compact
                    />

                    {/* Send / Stop button - circular */}
                    <Button
                      type={isBusy ? "button" : "submit"}
                      size="icon"
                      onClick={isBusy ? () => cancelSend() : undefined}
                      disabled={!isBusy && !inputValue.trim()}
                      className="h-8 w-8 rounded-full"
                      aria-label={isBusy ? "Stop generation" : "Send message"}
                      style={{
                        backgroundColor: isBusy
                          ? 'hsl(var(--destructive))'
                          : (!inputValue.trim() ? 'hsl(var(--muted-foreground))' : 'hsl(var(--primary))'),
                        color: isBusy ? 'hsl(var(--destructive-foreground))' : 'hsl(var(--primary-foreground))',
                        border: 'none',
                        transition: 'background-color 150ms ease-in-out',
                      }}
                    >
                      {isBusy ? (
                        <Square className="w-3.5 h-3.5 fill-current" />
                      ) : (
                        <ArrowUp className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </ExpandableChatFooter>
        </>
      )}

      {/* Agent Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent variant="light" className="sm:max-w-3xl p-0 gap-0">
          <UserAgentProfile />
        </DialogContent>
      </Dialog>
      <VoiceEnrollmentDialog
        open={showEnrollment}
        onOpenChange={setShowEnrollment}
        onComplete={onEnrollmentComplete}
      />
      {/* Skills Modal */}
      <SkillsModal open={showSkillsModal} onOpenChange={setShowSkillsModal} />
    </ExpandableChat>
  );
};
